import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { DateRange, ExpandAllState, HistorySidebarState, PipelineTab } from '../types';
import { deepLinkSearchPatch } from '../hooks/deepLinkRegistry';
import { getDefaultDateRange, mergePersistedPipelineState, shouldPersistDateRange } from './pipelinePersist';

// Search is scoped per pipeline tab (New Leads / Renewals / All Policies),
// so searching in one tab never leaks into another. Owner + date filters
// stay global across tabs.
export type SearchScope = 'new_leads' | 'renewals' | 'policies';

// Re-exported so existing importers (AuthenticatedHeader, PipelineEmptyState)
// keep their single entry point; the implementation lives with the persist
// helpers that also need it.
export { getDefaultDateRange };

/**
 * localStorage key for the global Created On / Owner filters (FTT-16444).
 * Matches the devtools name, the convention createQuotationQueryStore uses.
 */
const PIPELINE_PERSIST_KEY = 'voluntary-insurance-pipeline';

interface PipelineStore {
  // Global filters (apply across ALL tabs)
  ownerFilter: number[];
  dateRange: DateRange;
  searchByScope: Record<SearchScope, string>;
  // Record id from the last notification deep-link. While a scope's search
  // still equals it, the list query drops the date bounds so the record is
  // found regardless of age; any other search (or none) is date-bound as
  // usual. The date range itself is never emptied for a deep-link.
  deepLinkId: string | null;

  // Server-side sort per tab — the BE `order` param value ('' = BE default).
  // e.g. 'rf_name' asc / '-rf_name' desc (FTT-15421).
  sortByScope: Record<SearchScope, string>;

  // Expand/collapse all rows
  expandAllState: ExpandAllState;

  // Individually expanded row ids, scoped per tab so expansion survives list
  // refetches (an action inside an expanded row) and table remounts. Stored as
  // number[] (not Set) to stay serializable for devtools / future persistence.
  expandedRowsByScope: Record<SearchScope, number[]>;

  // History & Activity sidebar
  historySidebar: HistorySidebarState;

  // Actions — Global filters
  setOwnerFilter: (ids: number[]) => void;
  setDateRange: (range: DateRange) => void;
  setSearch: (scope: SearchScope, value: string) => void;
  /** Overwrite global filters for a notification deep-link record lookup (search scoped to the target tab). */
  applyDeepLinkSearch: (id: string, tab: PipelineTab) => void;
  setSort: (scope: SearchScope, value: string) => void;

  // Actions — Expand/collapse
  toggleExpandAll: () => void;
  toggleRowExpansion: (scope: SearchScope, id: number) => void;
  setExpandedRows: (scope: SearchScope, ids: number[]) => void;

  // Actions — History sidebar
  openHistorySidebar: (config: Omit<HistorySidebarState, 'isOpen'>) => void;
  closeHistorySidebar: () => void;
  setHistoryTab: (tab: HistorySidebarState['activeTab']) => void;
  setHistoryPolicyFilter: (filter: HistorySidebarState['policyFilter']) => void;
  /** Flag that a sidebar action hit the BE — the close handler reads this to
   *  refetch the host tables once. Called from mutation onSuccess callbacks. */
  markHistorySidebarDirty: () => void;
}

export const usePipelineStore = create<PipelineStore>()(
  devtools(
    persist(
      (set) => ({
        // Global filters
        ownerFilter: [],
        dateRange: getDefaultDateRange(),
        searchByScope: { new_leads: '', renewals: '', policies: '' },
        deepLinkId: null,
        sortByScope: { new_leads: '', renewals: '', policies: '' },

        // Expand/collapse
        expandAllState: 'collapsed',
        expandedRowsByScope: { new_leads: [], renewals: [], policies: [] },

        // History sidebar
        historySidebar: {
          isOpen: false,
          entityId: null,
          entityType: 'lead',
          quotationQueryId: null,
          quotationId: null,
          vmiStage: null,
          cmiStage: null,
          hasCmi: false,
          activeTab: 'all',
          policyFilter: 'all',
          manualQuotationStatus: null,
        },

        // Actions — Global filters
        setOwnerFilter: (ids) => set({ ownerFilter: ids }, false, 'setOwnerFilter'),

        setDateRange: (range) => set({ dateRange: range }, false, 'setDateRange'),

        setSearch: (scope, value) =>
          set((state) => ({ searchByScope: { ...state.searchByScope, [scope]: value } }), false, 'setSearch'),

        applyDeepLinkSearch: (id, tab) =>
          set((state) => deepLinkSearchPatch(id, tab, state.searchByScope), false, 'applyDeepLinkSearch'),

        setSort: (scope, value) =>
          set(
            (state) =>
              state.sortByScope[scope] === value ? state : { sortByScope: { ...state.sortByScope, [scope]: value } },
            false,
            'setSort',
          ),

        // Actions — Expand/collapse
        toggleExpandAll: () =>
          set(
            (state) => ({
              expandAllState: state.expandAllState === 'collapsed' ? 'expanded' : 'collapsed',
            }),
            false,
            'toggleExpandAll',
          ),

        toggleRowExpansion: (scope, id) =>
          set(
            (state) => {
              const current = state.expandedRowsByScope[scope];
              const next = current.includes(id) ? current.filter((rowId) => rowId !== id) : [...current, id];
              return { expandedRowsByScope: { ...state.expandedRowsByScope, [scope]: next } };
            },
            false,
            'toggleRowExpansion',
          ),

        setExpandedRows: (scope, ids) =>
          set(
            (state) => ({ expandedRowsByScope: { ...state.expandedRowsByScope, [scope]: ids } }),
            false,
            'setExpandedRows',
          ),

        // Actions — History sidebar
        openHistorySidebar: (config) =>
          set({ historySidebar: { ...config, isOpen: true, isDirty: false } }, false, 'openHistorySidebar'),

        closeHistorySidebar: () =>
          set(
            (state) => ({
              historySidebar: { ...state.historySidebar, isOpen: false },
            }),
            false,
            'closeHistorySidebar',
          ),

        setHistoryTab: (tab) =>
          set(
            (state) => ({
              historySidebar: { ...state.historySidebar, activeTab: tab },
            }),
            false,
            'setHistoryTab',
          ),

        setHistoryPolicyFilter: (filter) =>
          set(
            (state) => ({
              historySidebar: { ...state.historySidebar, policyFilter: filter },
            }),
            false,
            'setHistoryPolicyFilter',
          ),

        markHistorySidebarDirty: () =>
          set(
            (state) =>
              state.historySidebar.isDirty ? state : { historySidebar: { ...state.historySidebar, isDirty: true } },
            false,
            'markHistorySidebarDirty',
          ),
      }),
      {
        name: PIPELINE_PERSIST_KEY,
        // Allowlist, not a denylist: only the two global filters the ticket
        // asks for. Search stays out (a persisted deep-link id would replay
        // on every refresh), and so does every transient key — expanded rows,
        // the history sidebar, per-tab sort.
        partialize: (state: PipelineStore) => ({
          ownerFilter: state.ownerFilter,
          // Omitted while the range is the untouched default or a broken
          // half-range, so the merge falls back to a freshly computed default.
          ...(shouldPersistDateRange(state.dateRange) ? { dateRange: state.dateRange } : {}),
        }),
        // Same allowlist on the way back in — zustand's default merge would
        // otherwise spread any stray stored key over the live state.
        merge: (persistedState, current) => mergePersistedPipelineState(persistedState, current),
        // Without this hook a throw anywhere in the hydrate chain (merge
        // included) is routed to a no-op callback: the store silently keeps
        // its initial state and the filters just "aren't retained", with
        // nothing in the console to say why.
        onRehydrateStorage: () => (_state, error) => {
          if (error) console.error('[vi-pipeline] failed to restore the global filters', error);
        },
      },
    ),
    { name: PIPELINE_PERSIST_KEY },
  ),
);

// DEV-only debug hook: expose store on window for Playwright-driven UI testing.
// Remove before merging.
if (import.meta.env.DEV) {
  (window as unknown as { __pipelineStore?: typeof usePipelineStore }).__pipelineStore = usePipelineStore;
}
